import { useQuery, useQueryClient } from '@tanstack/react-query';
import { calcLine, D, parseMoney, PAYMENT_METHODS, SCALE, toScale } from '@makila/fiscal';
import { Ban, Banknote, CheckCircle2, FilePlus2, Pencil, Plus, ShoppingCart, Trash2, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ProductSuggest, SupplierPicker, type PickSupplier, useWarehouses } from '../components/pickers';
import {
  Alert, Badge, Button, Card, Chips, Empty, errorText, Field, Input, KeyValues, Loading, Modal, PageHeader, Pagination, SearchInput, Select,
  StatCard, StatusBadge, Switch, Table, Td, Textarea, Th, Toolbar, Tr, useConfirm, useToast,
} from '../components/ui';
import { del, get, patch, post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, mt, today } from '../lib/format';

const canon = (v: unknown) => (v === null || v === undefined || v === '' ? '' : String(Number(v)));
const num = (s: string) => parseMoney(s) ?? '0';

// ═══ Lista ═════════════════════════════════════════════════════════════════
export function PurchasesPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | 'draft' | 'posted' | 'open' | 'voided'>('');
  const [page, setPage] = useState(1);
  const params = new URLSearchParams({ page: String(page), pageSize: '25', q });
  if (status === 'open') params.set('open', '1'); else if (status) params.set('status', status);
  const r = useQuery({ queryKey: ['purchases', params.toString()], queryFn: () => get(`/purchases?${params}`) });
  return (
    <>
      <PageHeader eyebrow="Compras" title="Facturas de fornecedor" subtitle="Documentos recebidos: dão entrada em stock, actualizam o custo médio e alimentam as contas a pagar"
        actions={can('purchases:create') && <Link to="/compras/nova"><Button icon={<FilePlus2 className="size-4" />}>Registar factura</Button></Link>} />
      <Card padded={false}>
        <Toolbar>
          <SearchInput className="w-64" value={q} onChange={(x) => { setQ(x); setPage(1); }} placeholder="Fornecedor, NUIT ou n.º da factura" />
          <Chips value={status} onChange={(x) => { setStatus(x); setPage(1); }} options={[{ id: '', label: 'Todas' }, { id: 'draft', label: 'Rascunhos' }, { id: 'open', label: 'Por pagar' }, { id: 'posted', label: 'Lançadas' }, { id: 'voided', label: 'Anuladas' }]} />
        </Toolbar>
        {r.isLoading ? <div className="p-5"><Loading /></div> : !r.data?.items.length ? (
          <Empty icon={<ShoppingCart />} title="Sem facturas de fornecedor" action={can('purchases:create') && <Link to="/compras/nova"><Button>Registar a primeira</Button></Link>} />
        ) : (
          <>
            <Table>
              <thead><tr><Th>Fornecedor</Th><Th>N.º</Th><Th>Data</Th><Th>Vencimento</Th><Th>Estado</Th><Th className="text-right">IVA</Th><Th className="text-right">Total</Th><Th className="text-right">Em dívida</Th></tr></thead>
              <tbody>{r.data.items.map((p: any) => {
                const overdue = p.status === 'posted' && Number(p.pending) > 0 && p.due_date && p.due_date < today();
                return (
                  <Tr key={p.id} onClick={() => nav(`/compras/${p.id}`)}>
                    <Td><div className="font-medium">{p.supplier_name}</div><div className="text-xs text-[var(--muted)]">{p.supplier_nuit ?? ''}</div></Td>
                    <Td className="font-medium">{p.supplier_doc_number}</Td><Td>{date(p.doc_date)}</Td>
                    <Td className={overdue ? 'font-medium text-red-600' : ''}>{date(p.due_date)}</Td><Td><StatusBadge status={p.status} /></Td>
                    <Td className="num text-[var(--muted)]">{mt(p.tax_total)}</Td><Td className="num font-medium">{mt(p.gross_total)}</Td>
                    <Td className="num">{p.status === 'posted' ? (Number(p.pending) > 0.004 ? mt(p.pending) : <span className="text-emerald-700 dark:text-emerald-400">Paga</span>) : '—'}</Td>
                  </Tr>
                );
              })}</tbody>
            </Table>
            <Pagination page={page} pageSize={25} total={r.data.total} onPage={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

// ═══ Editor ════════════════════════════════════════════════════════════════
interface Line { key: string; productId: string | null; description: string; quantity: string; unitCost: string; discountPct: string; taxRate: number }
const newLine = (): Line => ({ key: crypto.randomUUID(), productId: null, description: '', quantity: '1', unitCost: '', discountPct: '', taxRate: 16 });

export function PurchaseEditPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const whs = useWarehouses();
  const existing = useQuery({ queryKey: ['purchase', id], enabled: !!id, queryFn: () => get(`/purchases/${id}`) });
  const preset = useQuery({ queryKey: ['supplier', sp.get('fornecedor')], enabled: !id && !!sp.get('fornecedor'), queryFn: () => get(`/suppliers/${sp.get('fornecedor')}`) });
  const [supplier, setSupplier] = useState<PickSupplier | null>(null);
  const [h, setH] = useState({ supplierDocNumber: '', docDate: today(), dueDate: '', movesStock: true, warehouseId: '', notes: '' });
  const [lines, setLines] = useState<Line[]>([newLine()]);
  const [busy, setBusy] = useState<'save' | 'post' | null>(null);
  const [loaded, setLoaded] = useState(!id);

  useEffect(() => { if (preset.data && !supplier) setSupplier(preset.data.supplier); }, [preset.data, supplier]);
  useEffect(() => {
    if (!existing.data || loaded) return;
    const { purchase: p, lines: ls } = existing.data;
    if (p.status !== 'draft') { nav(`/compras/${id}`, { replace: true }); return; }
    setSupplier({ id: p.supplier_id, name: p.supplier_name, nuit: p.supplier_nuit, code: '', city: '', payment_terms_days: null, default_payment_method: null });
    setH({ supplierDocNumber: p.supplier_doc_number, docDate: p.doc_date, dueDate: p.due_date ?? '', movesStock: p.moves_stock, warehouseId: p.warehouse_id ?? '', notes: p.notes ?? '' });
    setLines(ls.map((l: any) => ({ key: l.id, productId: l.product_id, description: l.description, quantity: canon(l.quantity), unitCost: canon(l.unit_cost), discountPct: Number(l.discount_pct) ? canon(l.discount_pct) : '', taxRate: Number(l.tax_rate) })));
    setLoaded(true);
  }, [existing.data, loaded, id, nav]);

  const calc = useMemo(() => lines.map((l) => { try { return calcLine({ quantity: toScale(num(l.quantity), SCALE.quantity), unitPrice: toScale(num(l.unitCost), SCALE.price), discountPct: toScale(num(l.discountPct), SCALE.pct), taxRate: l.taxRate }); } catch { return null; } }), [lines]);
  const totals = calc.reduce((t, c) => c ? { net: t.net.plus(c.netAmount), tax: t.tax.plus(c.taxAmount) } : t, { net: new D(0), tax: new D(0) });
  const setLine = (key: string, p: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...p } : l)));

  const save = async (andPost: boolean) => {
    if (!supplier) { toast('Escolha o fornecedor', 'error'); return; }
    if (andPost && !(await confirm({ title: 'Lançar a factura?', description: 'Depois de lançada dá entrada em stock (artigos com controlo), actualiza o custo médio e passa às contas a pagar. Só pode ser anulada, não editada.', confirmLabel: 'Lançar factura' }))) return;
    setBusy(andPost ? 'post' : 'save');
    const body = {
      supplierId: supplier.id, ...h, dueDate: h.dueDate || null, warehouseId: h.warehouseId || null, notes: h.notes || null,
      lines: lines.filter((l) => l.description.trim()).map((l) => ({ productId: l.productId, description: l.description, quantity: l.quantity, unitCost: l.unitCost || '0', discountPct: l.discountPct || '0', taxRate: l.taxRate })),
    };
    let pid = id;
    try {
      if (pid) await patch(`/purchases/${pid}`, body); else pid = (await post<{ id: string }>('/purchases', body)).id;
      if (andPost) { await post(`/purchases/${pid}/post`); toast('Factura lançada'); } else toast('Rascunho guardado');
      qc.invalidateQueries({ queryKey: ['purchases'] }); qc.invalidateQueries({ queryKey: ['nav-counts'] });
      nav(`/compras/${pid}`, { replace: true });
    } catch (e) {
      if (pid && !id) nav(`/compras/${pid}/editar`, { replace: true });
      toast(errorText(e), 'error');
    } finally { setBusy(null); }
  };

  if (!loaded) return <Loading />;
  return (
    <>
      <PageHeader eyebrow={<Link to="/compras" className="hover:underline">Compras</Link>} title={id ? 'Editar factura de fornecedor' : 'Registar factura de fornecedor'} subtitle="Copie os dados da factura que recebeu" />
      <div className="grid gap-6 2xl:grid-cols-[1fr_340px]">
        <div className="min-w-0 space-y-6">
          <Card title="Fornecedor e documento">
            <div className="space-y-4">
              <SupplierPicker value={supplier} onChange={setSupplier} autoFocus={!id} />
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="N.º da factura do fornecedor" required><Input value={h.supplierDocNumber} onChange={(e) => setH({ ...h, supplierDocNumber: e.target.value })} placeholder="Ex.: FT A/1234" /></Field>
                <Field label="Data da factura" required><Input type="date" max={today()} value={h.docDate} onChange={(e) => setH({ ...h, docDate: e.target.value })} /></Field>
                <Field label="Vencimento" hint="Vazio = prazo do fornecedor"><Input type="date" value={h.dueDate} onChange={(e) => setH({ ...h, dueDate: e.target.value })} /></Field>
              </div>
            </div>
          </Card>
          <Card title="Linhas" padded={false}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead><tr className="text-left text-[11px] font-semibold tracking-wider text-[var(--muted)] uppercase">
                  <th className="py-2.5 pl-5">Artigo / descrição</th><th className="w-24 px-2 text-right">Qtd.</th><th className="w-32 px-2 text-right">Custo unit.</th><th className="w-20 px-2 text-right">Desc. %</th><th className="w-26 px-2">IVA</th><th className="w-28 px-2 text-right">Total</th><th className="w-10 pr-5" />
                </tr></thead>
                <tbody>{lines.map((l, i) => (
                  <tr key={l.key} className="border-t border-[var(--border)] align-top">
                    <td className="py-2 pl-5"><ProductSuggest showCost value={l.description} ariaLabel={`Descrição da linha ${i + 1}`}
                      onText={(t) => setLine(l.key, { description: t, productId: null })}
                      onPick={(p) => setLine(l.key, { productId: p.id, description: p.name, unitCost: canon(Number(p.avg_cost) ? p.avg_cost : p.cost_price), taxRate: Number(p.tax_rate) })} />
                      {l.productId ? <Badge tone="teal">artigo ligado</Badge> : l.description && <span className="text-xs text-[var(--muted)]">sem artigo — não movimenta stock</span>}
                    </td>
                    <td className="px-2 py-2"><Input className="num h-9.5" inputMode="decimal" aria-label="Quantidade" value={l.quantity} onChange={(e) => setLine(l.key, { quantity: e.target.value })} /></td>
                    <td className="px-2 py-2"><Input className="num h-9.5" inputMode="decimal" aria-label="Custo unitário" value={l.unitCost} onChange={(e) => setLine(l.key, { unitCost: e.target.value })} /></td>
                    <td className="px-2 py-2"><Input className="num h-9.5" inputMode="decimal" aria-label="Desconto" value={l.discountPct} onChange={(e) => setLine(l.key, { discountPct: e.target.value })} /></td>
                    <td className="px-2 py-2"><Select className="h-9.5" aria-label="IVA" value={l.taxRate} onChange={(e) => setLine(l.key, { taxRate: Number(e.target.value) })}><option value={16}>16%</option><option value={5}>5%</option><option value={0}>Isento</option></Select></td>
                    <td className="num px-2 py-4 font-medium">{calc[i] ? mt(calc[i]!.totalAmount) : '—'}</td>
                    <td className="py-2 pr-5"><Button variant="ghost" size="sm" iconOnly icon={<Trash2 className="size-4" />} aria-label="Remover linha" onClick={() => setLines((ls) => ls.length > 1 ? ls.filter((x) => x.key !== l.key) : [newLine()])} /></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="border-t border-[var(--border)] px-5 py-3"><Button variant="soft" size="sm" icon={<Plus className="size-4" />} onClick={() => setLines([...lines, newLine()])}>Adicionar linha</Button></div>
          </Card>
        </div>
        <div className="min-w-0 space-y-6">
          <Card title="Totais">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-[var(--muted)]">Base</dt><dd className="tabular-nums">{mt(totals.net.toFixed(2))}</dd></div>
              <div className="flex justify-between"><dt className="text-[var(--muted)]">IVA dedutível</dt><dd className="tabular-nums">{mt(totals.tax.toFixed(2))}</dd></div>
              <div className="flex justify-between border-t border-[var(--border)] pt-2 text-lg font-bold"><dt>Total</dt><dd className="tabular-nums">{mt(totals.net.plus(totals.tax).toFixed(2))} MT</dd></div>
            </dl>
            <div className="mt-5 flex flex-col gap-2">
              <Button loading={busy === 'post'} disabled={!!busy} icon={<CheckCircle2 className="size-4" />} onClick={() => save(true)}>Guardar e lançar</Button>
              <Button variant="secondary" loading={busy === 'save'} disabled={!!busy} onClick={() => save(false)}>Guardar rascunho</Button>
            </div>
          </Card>
          <Card title="Stock">
            <div className="space-y-4">
              <Switch label="Dar entrada em stock" hint="Só para linhas ligadas a artigos com controlo de stock" checked={h.movesStock} onChange={(x) => setH({ ...h, movesStock: x })} />
              {h.movesStock && <Field label="Armazém"><Select value={h.warehouseId} onChange={(e) => setH({ ...h, warehouseId: e.target.value })}><option value="">Principal</option>{whs.data?.items.filter((w) => w.active).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</Select></Field>}
            </div>
          </Card>
          <Card title="Observações"><Textarea rows={3} aria-label="Observações" value={h.notes} onChange={(e) => setH({ ...h, notes: e.target.value })} /></Card>
        </div>
      </div>
    </>
  );
}

// ═══ Página da factura ═════════════════════════════════════════════════════
export function PurchaseViewPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [paying, setPaying] = useState(false);
  const r = useQuery({ queryKey: ['purchase', id], queryFn: () => get(`/purchases/${id}`) });
  if (r.isLoading || !r.data) return <Loading />;
  const { purchase: p, lines, payments } = r.data;
  const refresh = () => { qc.invalidateQueries({ queryKey: ['purchase', id] }); qc.invalidateQueries({ queryKey: ['purchases'] }); qc.invalidateQueries({ queryKey: ['nav-counts'] }); };
  const act = async (fn: () => Promise<unknown>, ok: string) => { try { await fn(); toast(ok); refresh(); } catch (e) { toast(errorText(e), 'error'); } };
  return (
    <>
      <PageHeader eyebrow={<Link to="/compras" className="hover:underline">Compras</Link>}
        title={<span className="flex flex-wrap items-center gap-3">{p.supplier_doc_number} <StatusBadge status={p.status} /></span>}
        subtitle={<Link className="hover:underline" to={`/fornecedores/${p.supplier_id}`}>{p.supplier_name}{p.supplier_nuit ? ` · NUIT ${p.supplier_nuit}` : ''}</Link>}
        actions={<>
          {p.status === 'draft' && can('purchases:edit') && <Link to={`/compras/${id}/editar`}><Button variant="secondary" icon={<Pencil className="size-4" />}>Editar</Button></Link>}
          {p.status === 'draft' && can('purchases:delete') && <Button variant="ghost" icon={<Trash2 className="size-4" />} onClick={async () => {
            if (await confirm({ title: 'Apagar o rascunho?', tone: 'danger', confirmLabel: 'Apagar' })) act(async () => { await del(`/purchases/${id}`); nav('/compras'); }, 'Rascunho apagado');
          }}>Apagar</Button>}
          {p.status === 'draft' && can('purchases:create') && <Button icon={<CheckCircle2 className="size-4" />} onClick={async () => {
            if (await confirm({ title: 'Lançar a factura?', description: 'Dá entrada em stock e passa às contas a pagar.', confirmLabel: 'Lançar' })) act(() => post(`/purchases/${id}/post`), 'Factura lançada');
          }}>Lançar</Button>}
          {p.status === 'posted' && Number(p.pending) > 0 && can('purchases:create') && <Button icon={<Banknote className="size-4" />} onClick={() => setPaying(true)}>Registar pagamento</Button>}
          {p.status === 'posted' && can('purchases:edit') && <Button variant="ghost" icon={<Ban className="size-4" />} onClick={async () => {
            const reason = await confirm({ title: 'Anular a factura de fornecedor?', description: 'O stock que entrou com esta factura sai de novo. Só é possível sem pagamentos activos.', tone: 'danger', confirmLabel: 'Anular', input: { label: 'Motivo', minLength: 5 } });
            if (typeof reason === 'string') act(() => post(`/purchases/${id}/void`, { reason }), 'Factura anulada');
          }}>Anular</Button>}
        </>} />
      {p.status === 'voided' && <div className="mb-5"><Alert tone="error" title="Factura anulada">{p.void_reason}</Alert></div>}
      <div className="grid gap-4 sm:grid-cols-4">
        <StatCard label="Total" value={`${mt(p.gross_total)} MT`} sub={`Base ${mt(p.net_total)} · IVA ${mt(p.tax_total)}`} />
        <StatCard label="Em dívida" value={p.status === 'posted' ? `${mt(p.pending)} MT` : '—'} tone={p.status === 'posted' && Number(p.pending) <= 0 ? 'good' : undefined} icon={<Wallet />} />
        <StatCard label="Data" value={date(p.doc_date)} sub={`Vence ${date(p.due_date)}`} />
        <StatCard label="Stock" value={p.moves_stock ? (p.warehouse_name ?? 'Armazém principal') : 'Sem entrada'} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card title="Linhas" className="lg:col-span-2" padded={false}>
          <Table>
            <thead><tr><Th>Descrição</Th><Th className="text-right">Qtd.</Th><Th className="text-right">Custo</Th><Th className="text-right">IVA</Th><Th className="text-right">Total</Th></tr></thead>
            <tbody>{lines.map((l: any) => (
              <tr key={l.id}>
                <Td><div className="font-medium">{l.description}</div>{l.product_code && <div className="text-xs text-[var(--muted)]">{l.product_code}</div>}</Td>
                <Td className="num">{Number(l.quantity)}</Td><Td className="num">{mt(l.unit_cost)}{Number(l.discount_pct) ? <div className="text-xs text-[var(--muted)]">−{Number(l.discount_pct)}%</div> : null}</Td>
                <Td className="num">{Number(l.tax_rate) ? `${Number(l.tax_rate)}%` : 'Isento'}</Td><Td className="num font-medium">{mt(l.total_amount)}</Td>
              </tr>
            ))}</tbody>
          </Table>
        </Card>
        <Card title="Pagamentos">
          {!payments.length ? <p className="text-sm text-[var(--muted)]">Sem pagamentos.</p> : (
            <ul className="divide-y divide-[var(--border)] text-sm">
              {payments.map((x: any) => (
                <li key={x.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div><div className="font-medium">{date(x.payment_date)} · {PAYMENT_METHODS[x.method as keyof typeof PAYMENT_METHODS]}</div><div className="text-xs text-[var(--muted)]">{x.reference ?? ''}</div></div>
                  <span className={`tabular-nums ${x.voided_at ? 'text-[var(--muted)] line-through' : 'font-medium'}`}>{mt(x.amount)}</span>
                </li>
              ))}
            </ul>
          )}
          {p.notes && <div className="mt-4"><KeyValues cols={1} items={[['Observações', p.notes]]} /></div>}
        </Card>
      </div>
      <SupplierPaymentModal open={paying} onClose={() => setPaying(false)} supplier={{ id: p.supplier_id, name: p.supplier_name, code: '', nuit: p.supplier_nuit, city: '', payment_terms_days: null, default_payment_method: null }} preselect={id} onSaved={refresh} />
    </>
  );
}

// ═══ Pagamento a fornecedor ════════════════════════════════════════════════
export function SupplierPaymentModal({ open, onClose, supplier: fixed, preselect, onSaved }: { open: boolean; onClose: () => void; supplier?: PickSupplier; preselect?: string; onSaved?: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [supplier, setSupplier] = useState<PickSupplier | null>(fixed ?? null);
  const [f, setF] = useState({ paymentDate: today(), method: 'TB', reference: '', notes: '' });
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const openList = useQuery({ queryKey: ['purchases-open', supplier?.id], enabled: open && !!supplier, queryFn: () => get(`/purchases/open/${supplier!.id}`) });
  useEffect(() => { if (open) { setSupplier(fixed ?? null); setAlloc({}); setF({ paymentDate: today(), method: fixed?.default_payment_method ?? 'TB', reference: '', notes: '' }); } }, [open, fixed]);
  useEffect(() => {
    if (preselect && openList.data) {
      const p = openList.data.items.find((x: any) => x.id === preselect);
      if (p) setAlloc({ [p.id]: canon(p.pending) });
    }
  }, [preselect, openList.data]);
  const total = Object.values(alloc).reduce((s, x) => s + Number(num(x) || 0), 0);
  const submit = async () => {
    setBusy(true);
    try {
      await post('/purchases/payments', {
        supplierId: supplier!.id, ...f, reference: f.reference || null, notes: f.notes || null,
        allocations: Object.entries(alloc).filter(([, x]) => Number(num(x)) > 0).map(([purchaseId, amount]) => ({ purchaseId, amount })),
      });
      toast('Pagamento registado');
      qc.invalidateQueries({ queryKey: ['purchases'] }); qc.invalidateQueries({ queryKey: ['supplier-payments'] }); qc.invalidateQueries({ queryKey: ['payables'] });
      onSaved?.(); onClose();
    } catch (e) { toast(errorText(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Pagamento a fornecedor" size="lg" icon={<Banknote />}
      footer={<><span className="mr-auto self-center text-sm">Total: <b className="tabular-nums">{mt(total)} MT</b></span><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button loading={busy} disabled={!supplier || total <= 0} onClick={submit}>Registar pagamento</Button></>}>
      <div className="space-y-5">
        {!fixed && <SupplierPicker value={supplier} onChange={(s) => { setSupplier(s); setAlloc({}); }} />}
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Data"><Input type="date" max={today()} value={f.paymentDate} onChange={(e) => setF({ ...f, paymentDate: e.target.value })} /></Field>
          <Field label="Meio"><Select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>{Object.entries(PAYMENT_METHODS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
          <Field label="Referência" hint="N.º da transferência/cheque"><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} /></Field>
        </div>
        {supplier && (openList.isLoading ? <Loading rows={2} /> : !openList.data?.items.length ? <Alert tone="success">Este fornecedor não tem facturas em dívida.</Alert> : (
          <div className="overflow-hidden rounded-xl border border-[var(--border)]">
            <table className="w-full text-sm">
              <thead><tr className="bg-[var(--surface-2)] text-left text-[11px] font-semibold tracking-wider text-[var(--muted)] uppercase"><th className="px-3 py-2">Factura</th><th className="px-3 py-2">Vencimento</th><th className="px-3 py-2 text-right">Em dívida</th><th className="w-48 px-3 py-2 text-right">A pagar</th></tr></thead>
              <tbody>{openList.data.items.map((p: any) => (
                <tr key={p.id} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2 font-medium">{p.supplier_doc_number}</td><td className="px-3 py-2">{date(p.due_date)}</td><td className="num px-3 py-2">{mt(p.pending)}</td>
                  <td className="px-3 py-2"><div className="flex justify-end gap-1">
                    <Input className="num h-8 w-28" inputMode="decimal" aria-label={`Pagar ${p.supplier_doc_number}`} value={alloc[p.id] ?? ''} onChange={(e) => setAlloc({ ...alloc, [p.id]: e.target.value })} />
                    <Button size="sm" variant="soft" onClick={() => setAlloc({ ...alloc, [p.id]: canon(p.pending) })}>Tudo</Button>
                  </div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ))}
      </div>
    </Modal>
  );
}

export function SupplierPaymentsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [paying, setPaying] = useState(false);
  const r = useQuery({ queryKey: ['supplier-payments', q, page], queryFn: () => get(`/purchases/payments?page=${page}&pageSize=25&q=${encodeURIComponent(q)}`) });
  const voidIt = async (x: any) => {
    const reason = await confirm({ title: 'Anular o pagamento?', description: `${mt(x.amount)} MT a ${x.supplier_name}. As facturas voltam a ficar em dívida.`, tone: 'danger', confirmLabel: 'Anular', input: { label: 'Motivo', minLength: 5 } });
    if (typeof reason !== 'string') return;
    try { await post(`/purchases/payments/${x.id}/void`, { reason }); toast('Pagamento anulado'); qc.invalidateQueries(); } catch (e) { toast(errorText(e), 'error'); }
  };
  return (
    <>
      <PageHeader eyebrow="Compras" title="Pagamentos a fornecedores" actions={can('purchases:create') && <Button icon={<Banknote className="size-4" />} onClick={() => setPaying(true)}>Novo pagamento</Button>} />
      <Card padded={false}>
        <Toolbar><SearchInput className="w-64" value={q} onChange={(x) => { setQ(x); setPage(1); }} placeholder="Fornecedor ou referência" /></Toolbar>
        {r.isLoading ? <div className="p-5"><Loading /></div> : !r.data?.items.length ? <Empty icon={<Banknote />} title="Sem pagamentos" /> : (
          <>
            <Table>
              <thead><tr><Th>Data</Th><Th>Fornecedor</Th><Th>Facturas</Th><Th>Meio</Th><Th>Referência</Th><Th className="text-right">Valor</Th><Th /></tr></thead>
              <tbody>{r.data.items.map((x: any) => (
                <tr key={x.id} className={x.voided_at ? 'opacity-60' : ''}>
                  <Td>{date(x.payment_date)}</Td><Td className="font-medium"><Link className="hover:underline" to={`/fornecedores/${x.supplier_id}`}>{x.supplier_name}</Link></Td>
                  <Td className="text-[var(--text-2)]">{x.documents}</Td><Td>{PAYMENT_METHODS[x.method as keyof typeof PAYMENT_METHODS]}</Td><Td className="text-[var(--muted)]">{x.reference ?? '—'}</Td>
                  <Td className={`num font-medium ${x.voided_at ? 'line-through' : ''}`}>{mt(x.amount)}</Td>
                  <Td className="text-right">{x.voided_at ? <Badge tone="red">Anulado</Badge> : can('purchases:edit') && <Button size="sm" variant="ghost" onClick={() => voidIt(x)}>Anular</Button>}</Td>
                </tr>
              ))}</tbody>
            </Table>
            <Pagination page={page} pageSize={25} total={r.data.total} onPage={setPage} />
          </>
        )}
      </Card>
      <SupplierPaymentModal open={paying} onClose={() => setPaying(false)} />
    </>
  );
}

export function PayablesPage() {
  const nav = useNavigate();
  const r = useQuery({ queryKey: ['payables'], queryFn: () => get('/purchases/payables') });
  if (r.isLoading || !r.data) return <Loading />;
  const rows = r.data.rows as Record<string, string>[];
  const sum = (k: string) => rows.reduce((s, x) => s + Number(x[k] ?? 0), 0);
  const cols: [string, string][] = [['current', 'A vencer'], ['d30', '1–30 dias'], ['d60', '31–60'], ['d60plus', '> 60 dias'], ['total', 'Total']];
  return (
    <>
      <PageHeader eyebrow="Compras" title="Contas a pagar" subtitle={`Antiguidade das dívidas a fornecedores em ${date(r.data.today)}`} />
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <StatCard label="Total em dívida" value={`${mt(sum('total'))} MT`} icon={<Wallet />} />
        <StatCard label="Vencido" tone={sum('total') - sum('current') > 0 ? 'warn' : 'good'} value={`${mt(sum('total') - sum('current'))} MT`} />
        <StatCard label="Fornecedores com dívida" value={rows.length} />
      </div>
      <Card padded={false}>
        {!rows.length ? <Empty icon={<Wallet />} title="Sem dívidas a fornecedores" /> : (
          <Table>
            <thead><tr><Th>Fornecedor</Th>{cols.map(([, l]) => <Th key={l} className="text-right">{l}</Th>)}</tr></thead>
            <tbody>
              {rows.map((x) => (
                <Tr key={x.supplier_id} onClick={() => nav(`/fornecedores/${x.supplier_id}`)}>
                  <Td><div className="font-medium">{x.name}</div><div className="text-xs text-[var(--muted)]">{x.nuit ?? ''}</div></Td>
                  {cols.map(([k]) => <Td key={k} className={`num ${k === 'total' ? 'font-semibold' : ''} ${['d60', 'd60plus'].includes(k) && Number(x[k]) > 0 ? 'text-red-600' : ''}`}>{Number(x[k]) ? mt(x[k]) : '—'}</Td>)}
                </Tr>
              ))}
              <tr className="bg-[var(--surface-2)] font-semibold"><Td>Total</Td>{cols.map(([k]) => <Td key={k} className="num">{mt(sum(k))}</Td>)}</tr>
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
